import { useState, useCallback, useEffect, useRef } from 'react';
import {
  rankWith,
  ControlProps,
  isDateTimeControl,
} from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { TextField, InputAdornment, IconButton, Popper, Paper, ClickAwayListener } from '@mui/material';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs, { Dayjs } from 'dayjs';
import { parseDateShortcut, formatDateTime } from '@/lib/dateShortcuts';
import { FieldLabel } from '../FieldLabel';

/**
 * Tester function to determine if this renderer should be used for datetime fields
 */
export const dateTimeRendererTester = rankWith(
  15, // Higher than default Material-UI renderers (1-2) but lower than lookups (20)
  isDateTimeControl
);

/**
 * Formats a dayjs value for display in the input field
 */
function formatForDisplay(value: Dayjs | null): string {
  if (!value || !value.isValid()) return '';
  return value.format('YYYY-MM-DD HH:mm');
}

/**
 * DateTime renderer component with F&O-style keyboard shortcuts
 *
 * Supports all date shortcuts plus time:
 * - `t` or `T` → today's date at 00:00
 * - `t 14:30` → today at 14:30
 * - `+N` / `-N` → relative day offsets
 * - `MMDD` → month + day (nearest date rule)
 * - etc.
 *
 * Time defaults to 00:00:00 when not specified
 */
export const DateTimeRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, description, required, errors, enabled, visible } = props;

  // Local input state (what user types)
  const [inputValue, setInputValue] = useState<string>('');
  // Whether the calendar picker is open
  const [pickerOpen, setPickerOpen] = useState(false);
  // Local error state for parsing errors
  const [parseError, setParseError] = useState<string | null>(null);
  // Ref for anchoring the popper
  const anchorRef = useRef<HTMLDivElement>(null);

  // Sync input value with model data
  useEffect(() => {
    if (data) {
      // Display as YYYY-MM-DD HH:mm
      const parsed = dayjs(data);
      if (parsed.isValid()) {
        setInputValue(formatForDisplay(parsed));
      } else {
        setInputValue(data);
      }
    } else {
      setInputValue('');
    }
  }, [data]);

  // Parse and commit value on blur/enter/tab
  const commitValue = useCallback(() => {
    const trimmed = inputValue.trim();

    // Empty input
    if (!trimmed) {
      setParseError(null);
      handleChange(path, undefined);
      return;
    }

    // Try to parse using shortcuts
    const result = parseDateShortcut(trimmed);

    if (result.success && result.value) {
      const formatted = formatDateTime(result.value.date);
      setParseError(null);
      // Update display to show the parsed value
      const displayValue = dayjs(result.value.date);
      setInputValue(formatForDisplay(displayValue));
      handleChange(path, formatted);
    } else {
      // Keep the invalid input, show error
      setParseError(result.error || 'Invalid date/time');
    }
  }, [inputValue, handleChange, path]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Clear parse error while typing
    setParseError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      commitValue();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't commit if clicking on the calendar
    if (anchorRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    commitValue();
  };

  const handlePickerChange = (value: Dayjs | null) => {
    if (value && value.isValid()) {
      const formatted = formatDateTime(value.toDate());
      setInputValue(formatForDisplay(value));
      setParseError(null);
      handleChange(path, formatted);
      setPickerOpen(false);
    }
  };

  const handleCalendarClick = () => {
    setPickerOpen((prev) => !prev);
  };

  const handleClickAway = () => {
    setPickerOpen(false);
  };

  if (!visible) {
    return null;
  }

  // Combine JSON Forms errors with parse errors
  const hasError = !!errors || !!parseError;
  const errorMessage = parseError || errors;

  // Parse current data for picker (null when empty so today isn't pre-selected)
  const parsedValue = data ? dayjs(data) : null;
  const pickerValue = parsedValue?.isValid() ? parsedValue : null;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div ref={anchorRef}>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          label={<FieldLabel label={label} description={description} />}
          required={required}
          disabled={!enabled}
          error={hasError}
          helperText={errorMessage}
          fullWidth
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleCalendarClick}
                  size="small"
                  edge="end"
                  disabled={!enabled}
                  sx={{ py: 0 }}
                >
                  <CalendarTodayIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Popper
          open={pickerOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          <Paper elevation={8}>
            <StaticDateTimePicker
              value={pickerValue}
              onChange={handlePickerChange}
              ampm={false}
              slotProps={{ actionBar: { actions: [] } }}
            />
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

/**
 * Connected datetime renderer with JSON Forms props
 */
export const DateTimeRenderer = withJsonFormsControlProps(DateTimeRendererComponent);

/**
 * Renderer entry for JSON Forms
 */
export const dateTimeRendererEntry = {
  tester: dateTimeRendererTester,
  renderer: DateTimeRenderer,
};
